import { useMemo, useState } from 'react';
import { makeStyles, tokens, Text } from '@fluentui/react-components';
import type { ScoreBarChartProps, ExecSeriesPoint } from './execTypes';

// Hand-rolled horizontal bar chart.
// * No third-party chart dependency — SVG only.
// * viewBox pixel-space (600 × H) + vector-effect: non-scaling-stroke for crisp
//   borders at any container width.
// * Tooltip is an HTML layer over the SVG (percentage coords), matching
//   TrendsChart idiom. <title> inside SVG for screen-reader fallback.

const PROVIDER_COLORS: Record<string, string> = {
  openai: '#10a37f',
  anthropic: '#c47a3b',
  'azure-openai': '#0078d4',
  'azure-ai-foundry': '#7719aa',
};

const FALLBACK_COLORS = ['#0078d4', '#10a37f', '#c47a3b', '#7719aa', '#e3008c', '#5c2d91'];

function colorFor(point: ExecSeriesPoint, index: number): string {
  if (point.color) return point.color;
  return PROVIDER_COLORS[point.key] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

const VIEW_W = 600;
const PAD_L = 0;  // label drawn in HTML layer

const useStyles = makeStyles({
  root: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '14px 16px',
    marginBottom: '16px',
  },
  header: {
    marginBottom: '10px',
  },
  chartWrap: {
    position: 'relative',
    width: '100%',
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    padding: '24px 0',
    textAlign: 'center' as const,
    fontStyle: 'italic',
  },
  tooltip: {
    position: 'absolute',
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '6px',
    boxShadow: tokens.shadow8,
    padding: '8px 10px',
    fontSize: '12px',
    lineHeight: 1.35,
    pointerEvents: 'none',
    transform: 'translate(-50%, -100%)',
    marginTop: '-8px',
    whiteSpace: 'nowrap' as const,
    zIndex: 2,
  },
});

interface HoverState {
  label: string;
  value: number;
  color: string;
  xPct: number;
  yPct: number;
}

const MAX_LABEL_LEN = 24;

function truncate(s: string): string {
  return s.length > MAX_LABEL_LEN ? s.slice(0, MAX_LABEL_LEN) + '…' : s;
}

export function ScoreBarChart({
  points,
  metric = 'overallScore',
  title,
  height = 220,
  sortDesc = true,
}: ScoreBarChartProps) {
  const s = useStyles();
  const [hover, setHover] = useState<HoverState | null>(null);

  const activePoints = useMemo(() => {
    const filtered = points.filter((p) => !p.isError);
    if (sortDesc) {
      return [...filtered].sort((a, b) => b[metric] - a[metric]);
    }
    return filtered;
  }, [points, metric, sortDesc]);

  const maxValue = useMemo(
    () => Math.max(...activePoints.map((p) => p[metric]), 0.0001),
    [activePoints, metric],
  );

  if (activePoints.length === 0) {
    return (
      <div className={s.root}>
        {title && (
          <div className={s.header}>
            <Text size={500} weight="semibold">{title}</Text>
          </div>
        )}
        <div className={s.empty}>No data</div>
      </div>
    );
  }

  const rowCount = activePoints.length;
  // Inner chart height (excluding header+padding) used for SVG rows
  const svgHeight = Math.max(height - (title ? 44 : 16), rowCount * 24);
  const rowH = svgHeight / rowCount;

  // Layout constants inside SVG pixel space
  const LABEL_W = 148;  // reserved width for label text column in px (VIEW_W coords)
  const VALUE_W = 60;   // reserved width for value text column
  const BAR_X = LABEL_W + 4;
  const BAR_MAX_W = VIEW_W - LABEL_W - VALUE_W - 8;

  const formatValue = (v: number) => (v * 100).toFixed(0) + '%';

  return (
    <div className={s.root}>
      {title && (
        <div className={s.header}>
          <Text size={500} weight="semibold">{title}</Text>
        </div>
      )}
      <div className={s.chartWrap}>
        <svg
          viewBox={`0 0 ${VIEW_W} ${svgHeight}`}
          role="img"
          aria-label={title ?? 'Score bar chart'}
          style={{ width: '100%', height: svgHeight, display: 'block' }}
        >
          {activePoints.map((point, idx) => {
            const color = colorFor(point, idx);
            const value = point[metric];
            const barW = (value / maxValue) * BAR_MAX_W;
            const cy = rowH * idx + rowH / 2;
            const barH = Math.min(rowH * 0.5, 18);
            const barY = cy - barH / 2;
            const xPct = ((BAR_X + barW / 2) / VIEW_W) * 100;
            const yPct = (cy / svgHeight) * 100;

            return (
              <g key={point.key}>
                {/* Label */}
                <text
                  x={PAD_L + 2}
                  y={cy}
                  fontSize={11}
                  dominantBaseline="central"
                  fill={tokens.colorNeutralForeground2}
                >
                  <title>{point.label}</title>
                  {truncate(point.label)}
                </text>

                {/* Bar background track */}
                <rect
                  x={BAR_X}
                  y={barY}
                  width={BAR_MAX_W}
                  height={barH}
                  rx={3}
                  fill={tokens.colorNeutralBackground3}
                  vectorEffect="non-scaling-stroke"
                />

                {/* Colored bar */}
                <rect
                  x={BAR_X}
                  y={barY}
                  width={Math.max(barW, 2)}
                  height={barH}
                  rx={3}
                  fill={color}
                  vectorEffect="non-scaling-stroke"
                >
                  <title>{`${point.label}: ${formatValue(value)}`}</title>
                </rect>

                {/* Value label */}
                <text
                  x={VIEW_W - VALUE_W + 4}
                  y={cy}
                  fontSize={11}
                  dominantBaseline="central"
                  fill={tokens.colorNeutralForeground1}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatValue(value)}
                </text>

                {/* Transparent hover target over bar */}
                <rect
                  x={BAR_X}
                  y={barY - 4}
                  width={BAR_MAX_W}
                  height={barH + 8}
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() =>
                    setHover({ label: point.label, value, color, xPct, yPct })
                  }
                  onMouseLeave={() => setHover(null)}
                />
              </g>
            );
          })}
        </svg>

        {hover && (
          <div
            className={s.tooltip}
            style={{ left: `${hover.xPct}%`, top: `${hover.yPct}%` }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 2 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: hover.color, flexShrink: 0 }} />
              {hover.label}
            </div>
            <div style={{ fontVariantNumeric: 'tabular-nums' }}>
              {(hover.value * 100).toFixed(1)}%
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
